import React, { useState } from 'react';
import PropTypes from 'prop-types';
import api from '../services/api';
import ConfirmModal from './ConfirmModal';
import Modal from './Modal';
import Toast from './Toast';
import useFormCRUD from '../hooks/useFormCRUD';

/**
 * Build a display path for a category (e.g. "Dairy > Milks > Oat Milk").
 *
 * @param {Object} c - Category object with level1, level2, level3
 * @returns {string} Formatted category path
 */
const getCategoryPath = (c) => {
  return [c.level1, c.level2, c.level3].filter(Boolean).join(' > ');
};

/**
 * Category management with full-width table and modal-based Add/Edit forms.
 * Categories use a 3-level hierarchy: Level 1 (Main) > Level 2 (Sub) > Level 3 (Detail).
 *
 * @param {Object} props
 * @param {Function} [props.onUpdate] - Callback invoked after create/delete/toggle operations
 * @returns {JSX.Element} The category table with Add/Edit modals
 */
function CategoryForm({ onUpdate }) {
  const [categorySearch, setCategorySearch] = useState('');

  const {
    items: categories,
    formData,
    setFormData,
    editingId,
    showForm,
    setShowForm,
    submitting,
    toast,
    confirmModal,
    handleSubmit,
    handleEdit,
    handleCancelEdit,
    handleDelete,
    handleToggleStatus,
    handleCloseToast,
    handleCloseConfirmModal,
  } = useFormCRUD({
    apiGetAll: () => api.getCategories({ include_inactive: true }),
    apiCreate: api.createCategory,
    apiUpdate: api.updateCategory,
    apiDelete: api.deleteCategory,
    apiToggleStatus: api.toggleCategoryStatus,
    initialFormData: { level1: '', level2: '', level3: '' },
    entityName: 'Category',
    useConfirmModal: true,
    getItemDisplayName: (item) => getCategoryPath(item),
    onUpdate,
  });

  const filteredCategories = categorySearch.trim()
    ? categories.filter(c =>
        getCategoryPath(c).toLowerCase().includes(categorySearch.toLowerCase())
      )
    : categories;

  const activeCount = categories.filter(c => c.status === 'active').length;

  return (
    <div>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={handleCloseToast}
        />
      )}
      {confirmModal && (
        <ConfirmModal
          isOpen={true}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          variant={confirmModal.variant}
          onConfirm={confirmModal.onConfirm}
          onCancel={handleCloseConfirmModal}
        />
      )}

      {/* Edit Category Modal */}
      <Modal
        isOpen={!!editingId}
        onClose={() => { handleCancelEdit(); setShowForm(false); }}
        title="Edit Category"
        size="medium"
      >
        <div className="category-form__help">
          <strong>How categories work:</strong> Use up to 3 levels to organize your spending.
          <strong> Level 1</strong> = Department (e.g., Dairy),
          <strong> Level 2</strong> = Sub-category (e.g., Milks),
          <strong> Level 3</strong> = Specific type (e.g., Oat Milk). Only Level 1 is required.
        </div>

        <form onSubmit={handleSubmit}>
          <div className="field">
            <label className="label" htmlFor="edit-category-level1">Level 1 (Main) <span className="req">*</span></label>
            <input
              id="edit-category-level1"
              className="input"
              type="text"
              value={formData.level1}
              onChange={(e) => setFormData({...formData, level1: e.target.value})}
              placeholder="e.g., Dairy"
              required
              aria-required="true"
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="edit-category-level2">Level 2 (Sub)</label>
            <input
              id="edit-category-level2"
              className="input"
              type="text"
              value={formData.level2}
              onChange={(e) => setFormData({...formData, level2: e.target.value})}
              placeholder="e.g., Milks"
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="edit-category-level3">Level 3 (Detail)</label>
            <input
              id="edit-category-level3"
              className="input"
              type="text"
              value={formData.level3}
              onChange={(e) => setFormData({...formData, level3: e.target.value})}
              placeholder="e.g., Oat Milk"
            />
          </div>

          <div className="settings-form__actions">
            <button type="submit" className="btn btn-primary btn-sm" disabled={submitting} style={{ flex: 1 }}>
              {submitting ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => { handleCancelEdit(); setShowForm(false); }}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      {/* Add Category Modal */}
      <Modal
        isOpen={showForm && !editingId}
        onClose={() => setShowForm(false)}
        title="Add Category"
        size="medium"
      >
        <div className="category-form__help">
          <strong>How categories work:</strong> Use up to 3 levels to organize your spending.
          <strong> Level 1</strong> = Department (e.g., Dairy),
          <strong> Level 2</strong> = Sub-category (e.g., Milks),
          <strong> Level 3</strong> = Specific type (e.g., Oat Milk). Only Level 1 is required.
        </div>

        <form onSubmit={handleSubmit}>
          <div className="field">
            <label className="label" htmlFor="category-level1">Level 1 (Main) <span className="req">*</span></label>
            <input
              id="category-level1"
              className="input"
              type="text"
              value={formData.level1}
              onChange={(e) => setFormData({...formData, level1: e.target.value})}
              placeholder="e.g., Dairy"
              required
              aria-required="true"
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="category-level2">Level 2 (Sub)</label>
            <input
              id="category-level2"
              className="input"
              type="text"
              value={formData.level2}
              onChange={(e) => setFormData({...formData, level2: e.target.value})}
              placeholder="e.g., Milks"
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="category-level3">Level 3 (Detail)</label>
            <input
              id="category-level3"
              className="input"
              type="text"
              value={formData.level3}
              onChange={(e) => setFormData({...formData, level3: e.target.value})}
              placeholder="e.g., Oat Milk"
            />
          </div>

          <div className="settings-form__actions">
            <button type="submit" className="btn btn-primary btn-sm" disabled={submitting} style={{ flex: 1 }}>
              {submitting ? 'Saving...' : 'Add Category'}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setShowForm(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      <div className="card">
        <div className="settings-form__table-header">
          <h3 className="settings-form__section-title" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
            Categories ({activeCount} active{categories.length !== activeCount ? `, ${categories.length - activeCount} inactive` : ''})
          </h3>
          <div style={{ display: 'flex', gap: 'var(--sp2)', alignItems: 'center' }}>
            {categories.length > 3 && (
              <input
                className="input settings-form__search-input"
                type="text"
                placeholder="Search..."
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                aria-label="Search categories"
              />
            )}
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>+ Add Category</button>
          </div>
        </div>

        {categories.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state__title">No categories yet</div>
            <div className="empty-state__description">Add your first category to get started.</div>
          </div>
        ) : (
          <div className="tbl-wrap tbl-wrap--md">
            <table>
              <thead>
                <tr>
                  <th scope="col">Level 1</th>
                  <th scope="col">Level 2</th>
                  <th scope="col">Level 3</th>
                  <th scope="col">Status</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                {filteredCategories.map(c => (
                  <tr
                    key={c.id}
                    className={`${c.status === 'inactive' ? 'row-inactive' : ''} ${editingId === c.id ? 'table-row--editing' : ''}`.trim() || undefined}
                  >
                    <td>{c.level1}</td>
                    <td>{c.level2 || <span className="settings-form__meta">&mdash;</span>}</td>
                    <td>{c.level3 || <span className="settings-form__meta">&mdash;</span>}</td>
                    <td>
                      <span className={`status-badge status-${c.status}`}>
                        {c.status === 'active' ? '\u2713' : '\u2717'} {c.status}
                      </span>
                    </td>
                    <td>
                      <div className="settings-form__action-btns">
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleEdit(c)}
                          aria-label={'Edit ' + getCategoryPath(c)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleToggleStatus(c.id)}
                          aria-label={(c.status === 'active' ? 'Disable ' : 'Enable ') + getCategoryPath(c)}
                          style={{ color: c.status === 'active' ? 'var(--warning)' : 'var(--success)' }}
                        >
                          {c.status === 'active' ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleDelete(c.id, getCategoryPath(c))}
                          aria-label={'Delete ' + getCategoryPath(c)}
                          style={{ color: 'var(--error)' }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

CategoryForm.propTypes = {
  /** Callback invoked after create/delete/toggle operations */
  onUpdate: PropTypes.func,
};

export default CategoryForm;
